import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { combinedCampaignsSelector, combinedLineItemsSelector } from '../../../../../modules/selectors/combinedData'
import { UPDATE_COMBINED_CAMPAIGN } from '../../../../../modules/actions/combinedData'
import { rightSelector } from '../../../../../modules/selectors/rights'

import TikTokCampaignBudgetSectionForm from '../../../../ReusableFormSections/reusableBudgetSections/TikTokBudgetSections/TikTokCampaignBudgetSection/TikTokCampaignBudgetSectionForm'
import TikTokCampaignBudgetSectionPreview from '../../../../ReusableFormSections/reusableBudgetSections/TikTokBudgetSections/TikTokCampaignBudgetSection/TikTokCampaignBudgetSectionPreview'
import TikTokCampaignImpressionsBudgetSectionForm from './TIkTokCampaignImpressionsBudgetSection/TIkTokCampaignImpressionsBudgetForm'
import TikTokCampaignImpressionsBudgetPreview from './TIkTokCampaignImpressionsBudgetSection/TikTokCampaignImpressionsBudgetPreview'
import TikTokLineItemBudgetSectionPreview from '../../../../ReusableFormSections/reusableBudgetSections/TikTokBudgetSections/TikTokLineItemBudgetSection/TikTokLineItemBudgetSectionPreview'
import TikTokLineItemBudgetSectionForm from '../../../../ReusableFormSections/reusableBudgetSections/TikTokBudgetSections/TikTokLineItemBudgetSection/TikTokLineItemBudgetSectionForm'
import TikTokLineItemImpressionsBudgetPreview from './TikTokLineItemImpressionsBudgetSection/TikTokLineItemImpressionsBudgetPreview'
import TikTokLineItemImpressionsBudgetForm from './TikTokLineItemImpressionsBudgetSection/TikTokLineItemImpressionsBudgetForm'

import useRightBudgetAndImpressionsCalculation from '../../../UploadAdCreative/useRightBudgetAndImpressionsCalculation'
import { insertIf } from '../../../../../helpers/common'

import { CAMPAIGN_BUDGET } from '../../../CampaignForms/fields'
import {
  BUDGET_DAILY_LINE_ITEM,
  BUDGET_LIFETIME_LINE_ITEM
} from '../../../CombinedFacebookTiktokCreate/CreateFormSelector/CampaignCreateForm/fields'
import { LINE_ITEM_BUDGET } from '../../../../Facebook/LineItemForms/LineItemFacebookEdit/fields'
import {
  DIGITAL_ALLOCATION_TYPE_BUDGET,
  DIGITAL_ALLOCATION_TYPE_IMPRESSIONS
} from '../../../Partnerships/RightForms/fields'
import { BUDGET } from '../../../../Tiktok/CampaignForms/CampaignTikTokEdit/fields'

export const useTikTokCampaignBudgetEditSections = selectedEditItemId => {
  const combinedCampaigns = useSelector(combinedCampaignsSelector)
  const combinedLineItemsGroupedByCampaigns = useSelector(combinedLineItemsSelector)
  const { allocation_type: rightAllocationType } = useSelector(rightSelector)

  const { remainingBudget, remainingImpressions } = useRightBudgetAndImpressionsCalculation()
  const isBudgetAllocationType = rightAllocationType === DIGITAL_ALLOCATION_TYPE_BUDGET
  const isImpressionAllocationType = rightAllocationType === DIGITAL_ALLOCATION_TYPE_IMPRESSIONS

  const { campaigns = [] } = combinedCampaigns
  const selectedCampaign = campaigns.find(campaign => String(campaign.id) === String(selectedEditItemId))

  const selectedCombinedLineItemsCampaign =
    combinedLineItemsGroupedByCampaigns.find(({ campaign_id }) => String(campaign_id) === String(selectedEditItemId)) ||
    {}

  const { line_items: combinedLineItems = [] } = selectedCombinedLineItemsCampaign

  const {
    [BUDGET]: budget,
    [BUDGET_LIFETIME_LINE_ITEM]: budgetLifetimeLineItem,
    [BUDGET_DAILY_LINE_ITEM]: budgetDailyLineItem,
    ad_account_id: adAccountId
  } = selectedCampaign || {}

  const isCampaignLevelBudget = !!budget
  const isLineItemLevelBudget = !!budgetLifetimeLineItem || !!budgetDailyLineItem

  const sectionsInitialOpenStates = {
    ...(isCampaignLevelBudget ? { [CAMPAIGN_BUDGET]: true } : {})
  }

  const lineItemsBudgetSections = useMemo(() => {
    return combinedLineItems.map(lineItem => {
      return {
        sectionName: `${LINE_ITEM_BUDGET}_${lineItem.id}`,
        title: `${lineItem.name}`,
        PreviewComponent: <TikTokLineItemBudgetSectionPreview lineItem={lineItem} />,
        FormComponent: (
          <TikTokLineItemBudgetSectionForm
            lineItem={lineItem}
            providedAdAccountId={adAccountId}
            remainingBudget={remainingBudget}
          />
        )
      }
    })
  }, [combinedLineItems, remainingBudget, adAccountId])

  const lineItemsImpressionsBudgetSections = useMemo(() => {
    return combinedLineItems.map(lineItem => {
      return {
        sectionName: `${LINE_ITEM_BUDGET}_${lineItem.id}`,
        title: `${lineItem.name}`,
        PreviewComponent: <TikTokLineItemImpressionsBudgetPreview lineItem={lineItem} />,
        FormComponent: (
          <TikTokLineItemImpressionsBudgetForm
            lineItem={lineItem}
            providedAdAccountId={adAccountId}
            remainingImpressions={remainingImpressions}
          />
        )
      }
    })
  }, [combinedLineItems, remainingImpressions, adAccountId])

  const campaignBudgetSection = useMemo(
    () => ({
      sectionName: CAMPAIGN_BUDGET,
      title: 'Budget',
      PreviewComponent: <TikTokCampaignBudgetSectionPreview campaign={selectedCampaign} />,
      FormComponent: (
        <TikTokCampaignBudgetSectionForm
          campaign={selectedCampaign}
          providedAdAccountId={adAccountId}
          updateType={UPDATE_COMBINED_CAMPAIGN}
          remainingBudget={remainingBudget}
        />
      )
    }),
    [selectedCampaign, remainingBudget, adAccountId]
  )

  const campaignImpressionsBudgetSection = useMemo(() => {
    return {
      sectionName: 'campaign_impression_budget',
      title: 'Impression',
      PreviewComponent: <TikTokCampaignImpressionsBudgetPreview campaign={selectedCampaign} />,
      FormComponent: (
        <TikTokCampaignImpressionsBudgetSectionForm
          campaign={selectedCampaign}
          remainingImpressions={remainingImpressions}
          providedAdAccountId={adAccountId}
          updateType={UPDATE_COMBINED_CAMPAIGN}
        />
      )
    }
  }, [remainingImpressions, selectedCampaign, adAccountId])

  const impressionsAllocationSections = useMemo(
    () => [
      // campaign impression budget section
      ...insertIf(isCampaignLevelBudget, campaignImpressionsBudgetSection),
      // lineItem impression budget sections
      ...insertIf(isLineItemLevelBudget, ...lineItemsImpressionsBudgetSections)
    ],
    [isLineItemLevelBudget, lineItemsImpressionsBudgetSections, isCampaignLevelBudget, campaignImpressionsBudgetSection]
  )

  const budgetAllocationSections = useMemo(
    () => [
      // campaign budget section,
      ...insertIf(isCampaignLevelBudget, campaignBudgetSection),
      // lineItem budget sections
      ...insertIf(isLineItemLevelBudget, ...lineItemsBudgetSections)
    ],
    [isCampaignLevelBudget, campaignBudgetSection, isLineItemLevelBudget, lineItemsBudgetSections]
  )

  const editSections = useMemo(() => {
    return [
      ...(isBudgetAllocationType ? budgetAllocationSections : []),
      ...(isImpressionAllocationType ? impressionsAllocationSections : [])
    ]
  }, [isBudgetAllocationType, isImpressionAllocationType, budgetAllocationSections, impressionsAllocationSections])

  return { editSections, sectionsInitialOpenStates }
}
